import React, { FC, useEffect, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { syncTransportPackageTypesFetch } from 'src/resources/transportPackageTypesList/reducer';
import { RootStoreType } from 'src/types/store';
import TableSimple from 'src/components/TableSimple';
import TopBlock from './TopBlock';
import { allColumns } from './constants';
import { useCheckPermission } from 'src/utils/checkPermission';
import api from '@api';
import { GeisPackingType } from 'src/types/transportPackageType';
import { addMessage } from 'src/resources/alertMessages/reducer';
import StoreIcon from '@mui/icons-material/Store';

type GeisRow = GeisPackingType & {
    PACKAGE_TYPE_SSRCS?: string[];
    PACKAGE_TYPES_LABEL?: string;
};

const GeisPackingTypesListContainer: FC = () => {
    const dispatch = useDispatch();
    const loadingSync = useSelector((state: RootStoreType) => state.transportPackageTypesList.loading);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);

    const [rows, setRows] = useState<GeisRow[]>([]);
    const [loading, setLoading] = useState(false);

    const permissionSync = useCheckPermission('TRANSPORT_PACKAGE_TYPE.SYNC');
    const permissionDetail = useCheckPermission('TRANSPORT_PACKAGE_TYPE.DETAIL');
    const permissionList = useCheckPermission('TRANSPORT_PACKAGE_TYPE.LIST');
    const permissionUpdate = useCheckPermission('TRANSPORT_PACKAGE_TYPE.UPDATE');
    const canOpenDetail = permissionDetail || permissionList || permissionUpdate;

    const loadList = async () => {
        setLoading(true);
        try {
            const res = await api('get', 'api/secure/transports/geis-packing-types?active=0');
            const data: GeisRow[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data)
                  ? res.data
                  : [];
            setRows(
                data.map((row) => {
                    const ssrcs = row.PACKAGE_TYPE_SSRCS ?? (row.PACKAGE_TYPE_SSRC ? [row.PACKAGE_TYPE_SSRC] : []);
                    return {
                        ...row,
                        PACKAGE_TYPE_SSRCS: ssrcs,
                        PACKAGE_TYPES_LABEL: ssrcs.length ? ssrcs.join(', ') : '—',
                    };
                })
            );
        } catch {
            setRows([]);
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('geisPackingTypes.message.loadError'),
                })
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('geisPackingTypes.title') }));
        void loadList();
    }, []);

    const handleSync = () => {
        dispatch(syncTransportPackageTypesFetch());
    };

    const wasSyncing = useRef(false);
    useEffect(() => {
        if (loadingSync) {
            wasSyncing.current = true;
            return;
        }
        if (wasSyncing.current) {
            wasSyncing.current = false;
            void loadList();
        }
    }, [loadingSync]);

    return (
        <div data-testid="geis-packing-types-list">
            <TopBlock
                reloadList={() => void loadList()}
                syncGeis={handleSync}
                permissionSync={permissionSync}
            />
            <TableSimple
                rowIdKey="CODE"
                rows={rows}
                loading={loading || loadingSync}
                columsLabelPrefix="geisPackingTypes.table"
                allColumns={allColumns}
                initSort={{ column: 'CODE', direction: 'ASC' }}
                fullHeight
                heightVariant="top-tab-2"
                selectOptions={{ boolNumberCodes }}
                rowActions={[
                    {
                        icon: <StoreIcon />,
                        label: translate('geisPackingTypes.action.detail'),
                        getLink: (row) => `/transports/geis-packing-types/${row.CODE}/`,
                        hiddenFce: () => !canOpenDetail,
                    },
                ]}
            />
        </div>
    );
};

export default GeisPackingTypesListContainer;
